import "react-datepicker/dist/react-datepicker.css"
import './index.css'

import React, { useEffect, useMemo, useState } from 'react'
import DatePicker from "react-datepicker"
import { Doc } from "@/services/model/docs"
import { Translates } from '@/services/repository/i18n'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'

import { RDropdown } from '@/components/forms/common/CustomDropdown'
import { DateRange } from '../dateTimeRange'
import { formatDate } from '@/common/time'

import { Location, NavigateFunction } from 'react-router-dom';

export enum StretchOfTime {
  OneDay = '1d',
  ThreeDays = '3d',
  FiveDays = '5d',
  SevenDays = '7d',
  TwoWeeks = '2w',
  ThreeWeeks = '3w',
  OneMonth = '1m',
  TwoMonths = '2m',
  ThreeMonths = '3m'
}

export type StretchOfTimeValue = 
  | StretchOfTime.OneDay
  | StretchOfTime.ThreeDays
  | StretchOfTime.FiveDays
  | StretchOfTime.SevenDays
  | StretchOfTime.TwoWeeks
  | StretchOfTime.ThreeWeeks
  | StretchOfTime.OneMonth
  | StretchOfTime.TwoMonths
  | StretchOfTime.ThreeMonths;

export function stretchOfTimeToSeconds(value: StretchOfTimeValue): number {
  switch (value) {
    case StretchOfTime.OneDay:
      return 60 * 60 * 24;
    case StretchOfTime.ThreeDays:
      return 60 * 60 * 24 * 3;
    case StretchOfTime.FiveDays:
      return 60 * 60 * 24 * 5;
    case StretchOfTime.SevenDays:
      return 60 * 60 * 24 * 7;
    case StretchOfTime.TwoWeeks:
      return 60 * 60 * 24 * 14;
    case StretchOfTime.ThreeWeeks:
      return 60 * 60 * 24 * 21;
    case StretchOfTime.OneMonth:
      return 60 * 60 * 24 * 30;
    case StretchOfTime.TwoMonths:
      return 60 * 60 * 24 * 60;
    case StretchOfTime.ThreeMonths:
      return 60 * 60 * 24 * 90;
    default:
      throw new Error('Invalid StretchOfTimeValue');
  }
}

export type DatePeriod = {
  for: Date | undefined
  length: StretchOfTimeValue
}

var stretchOfTime: string[] = Object.values(StretchOfTime)

export function decodeDatePeriod(params: URLSearchParams): DatePeriod {
  function parseStretchOfTime(value: string | null): StretchOfTimeValue | undefined {
    if (!value) return undefined;
    const allowed = Object.values(StretchOfTime) as string[];
    return allowed.includes(value) ? (value as StretchOfTimeValue) : undefined;
  }
  function parseQueryDate(value: string | null): Date | undefined {
    if (!value) return undefined;
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return {
    for: parseQueryDate(params.get('for')),
    length: parseStretchOfTime(params.get('length')) ?? StretchOfTime.OneDay, // fallback/default if missing
  };
}

export function datePeriodToQueryParams(period: DatePeriod): Record<string, string> {
  const params: Record<string, string> = {};
  if (period.for) {
    params['for'] = period.for.toISOString() //.slice(0, 10); // to YYYY-MM-DD
  }
  params['length'] = period.length; // enum is string union, safe to use as value
  return params;
}

// Helper for getting as query string:
export function datePeriodToQueryString(period: DatePeriod): string {
  const params = datePeriodToQueryParams(period);
  const searchParams = new URLSearchParams(params);
  return searchParams.toString();
}

export function updateDatePeriodSearch(
  search: string,
  navigate: NavigateFunction,
  period: DatePeriod
) {
  // Parse current search params
  const params = new URLSearchParams(location.search);

  // Apply/overwrite 'for' and 'length'
  const dateParams = datePeriodToQueryParams(period);
  Object.entries(dateParams).forEach(([key, value]) => {
    params.set(key, value);
  });

  // Navigate, preserving all others
  navigate(
    {
      pathname: location.pathname,
      search: params.toString(),
    },
    { replace: true }
  );
}

export type DatePeriodSetter = (period: DatePeriod) => void

export function datePeriodToRange(period: DatePeriod): DateRange {
  const to = new Date(period.for ?? Date.now());
  const from = new Date(to);

  switch (period.length) {
    case StretchOfTime.OneDay:
      from.setDate(from.getDate() - 1);
      break;
    case StretchOfTime.ThreeDays:
      from.setDate(from.getDate() - 3);
      break;
    case StretchOfTime.FiveDays:
      from.setDate(from.getDate() - 5);
      break;
    case StretchOfTime.SevenDays:
      from.setDate(from.getDate() - 7);
      break;
    case StretchOfTime.TwoWeeks:
      from.setDate(from.getDate() - 14);
      break;
    case StretchOfTime.ThreeWeeks:
      from.setDate(from.getDate() - 21);
      break;
    case StretchOfTime.OneMonth:
      from.setMonth(from.getMonth() - 1);
      break;
    case StretchOfTime.TwoMonths:
      from.setMonth(from.getMonth() - 2);
      break;
    case StretchOfTime.ThreeMonths:
      from.setMonth(from.getMonth() - 3);
      break;
  }

  return { from, to };
}

export function DateTimePeriodBody({ services, session, translates, period, setter, onClose }: { translates: Translates, services: Services, period: DatePeriod, setter: DatePeriodSetter, session: CurrentSession | undefined, onClose: () => void }) {
  const [newPeriod, setNewPeriod] = useState(period)

  // @ts-ignore: Unreachable code error
  const ReactDatePicker = DatePicker.default ?? DatePicker;
  /* tslint:enable */

  let now = new Date()
  // let minTime = new Date(0)
  // minTime.setHours(0, 0, 0, 0)

  const dateRange: DateRange | undefined = useMemo(() => {
    return newPeriod ? datePeriodToRange(newPeriod) : undefined
  }, [newPeriod])

  return (
    <form className="datetimeperiod-popup-body main-form" onSubmit={(e) => {
      e.preventDefault()
      setter(newPeriod)
      onClose()
    }}>
      {/* From Date */}
      <div className="form-field">
        <label htmlFor="for-date">{translates.for('popup.datetime_period.for')}:</label>
        <ReactDatePicker
          id="for-date"
          name="for"
          selected={newPeriod.for ? new Date(newPeriod.for) : null}
          onChange={(date) => {
            if(!date) {
              setNewPeriod({
                ...newPeriod,
                for: undefined
              })
              return
            }
            date = new Date(date)
            setNewPeriod({
              ...newPeriod,
              for: date
            })
          }}
          showTimeSelect={true}
          timeIntervals={15}
          timeFormat="HH:mm"
          dateFormat="yyyy-MM-dd HH:mm"
          maxDate={now}
          // minTime={minTime}
          // maxTime={now}
          isClearable={true}
          // placeholderText="Select start date and time"
          className="border rounded p-2"
          portalId="tooltip-wrap"
          placeholderText={translates.for('popup.datetime_period.now')}
        />
      </div>

      {/* To Date */}
      <div className="form-field">
        <label htmlFor="length-date">{translates.for('popup.datetime_period.length')}:</label>
        <RDropdown
          value={newPeriod.length}
          options={stretchOfTime.map((s) => ({ 
            value: s, 
            label: translates.for(`dicts.period_length.${s}`)
          }))}
          onChange={(length) => setNewPeriod({
            ...newPeriod,
            length: length as StretchOfTime
          })}
        />
      </div>
      { newPeriod.for !== undefined && dateRange?.from && dateRange.to ? <div className='summary-range'>
        { translates.for('popup.datetime_period.summary').replace('{from}', formatDate(dateRange.from)).replace('{to}', newPeriod.for === undefined ? translates.for('popup.datetime_period.now') : formatDate(newPeriod.for)) }
      </div> : null }
      <button className="submit-button" type="submit">{translates.for('popup.datetime_period.submit')}</button>
    </form>
  );
}